import { useState } from "react";

import {
  type Column,
  EmptyState,
  Pill,
  Select,
  type TabItem,
  Table,
  Tabs,
} from "@/components/common";
import { useAdminAuthLogs, useAuditErrorLogs } from "@/hooks/useAuthLogs";
import type { AuditErrorLog, AuthLog } from "@/types/authLog";
import { AUTH_EVENT_LABEL, authEventLabel, formatDateTime } from "@/utils/authEvents";

type Sub = "auth" | "errors";

const EVENT_OPTIONS = [
  { value: "", label: "Todos os eventos" },
  ...Object.entries(AUTH_EVENT_LABEL).map(([value, label]) => ({ value, label })),
];

function AuthLogsTab() {
  const [event, setEvent] = useState("");
  const { data: logs = [], isLoading } = useAdminAuthLogs(
    event ? { event_type: event } : undefined,
  );

  const columns: Column<AuthLog>[] = [
    {
      id: "event",
      header: "Evento",
      cell: (l) => (
        <Pill tone={l.success ? "success" : "danger"}>{authEventLabel(l.event_type)}</Pill>
      ),
    },
    {
      id: "user",
      header: "Usuário",
      cell: (l) => (
        <span className="td-muted">{l.user_id != null ? `Usuário #${l.user_id}` : "—"}</span>
      ),
    },
    { id: "ip", header: "IP", cell: (l) => <span className="td-small">{l.ip ?? "—"}</span> },
    {
      id: "when",
      header: "Quando",
      cell: (l) => <span className="td-faded">{formatDateTime(l.created_at)}</span>,
    },
  ];

  return (
    <>
      <div className="emp-toolbar">
        <Select
          value={event}
          onChange={setEvent}
          options={EVENT_OPTIONS}
        />
      </div>
      <Table
        rows={logs}
        columns={columns}
        getKey={(l) => String(l.id)}
        empty={
          <EmptyState
            icon="shield-check"
            size="lg"
            title={isLoading ? "Carregando…" : "Sem registros"}
            description={isLoading ? undefined : "Eventos de autenticação aparecerão aqui."}
          />
        }
      />
    </>
  );
}

function ErrorLogsTab() {
  const { data: errors = [], isLoading } = useAuditErrorLogs();

  const columns: Column<AuditErrorLog>[] = [
    {
      id: "status",
      header: "Status",
      cell: (e) => <Pill tone="danger">{e.status_code}</Pill>,
    },
    {
      id: "route",
      header: "Rota",
      cell: (e) => (
        <span className="cnpj">
          {e.method ?? "—"} {e.path ?? "—"}
        </span>
      ),
    },
    {
      id: "error",
      header: "Erro",
      cell: (e) => <span className="td-muted">{e.error_type ?? "—"}</span>,
    },
    {
      id: "when",
      header: "Quando",
      cell: (e) => <span className="td-faded">{formatDateTime(e.created_at)}</span>,
    },
  ];

  return (
    <Table
      rows={errors}
      columns={columns}
      getKey={(e) => String(e.id)}
      empty={
        <EmptyState
          icon="activity"
          size="lg"
          title={isLoading ? "Carregando…" : "Sem erros"}
          description={isLoading ? undefined : "Erros 5xx do servidor aparecerão aqui."}
        />
      }
    />
  );
}

/** Platform-admin view of the authentication trail + persisted 5xx errors. */
export function AccessLogsPanel() {
  const [sub, setSub] = useState<Sub>("auth");
  const tabs: TabItem<Sub>[] = [
    { id: "auth", label: "Autenticação" },
    { id: "errors", label: "Erros 5xx" },
  ];
  return (
    <>
      <Tabs items={tabs} active={sub} onChange={setSub} />
      {sub === "auth" ? <AuthLogsTab /> : <ErrorLogsTab />}
    </>
  );
}
